import type { QueryClient } from '@tanstack/react-query';
import type { Socket } from 'socket.io-client';
import type { Namespace } from '@/state/connectionStore';

/**
 * Socket event → app state wiring (research.md §5). Each feature registers
 * the events it cares about (e.g. `message:new` → the messages cache); the
 * socket layer attaches every binding to each new connection.
 */
export interface BindingContext {
  queryClient: QueryClient;
  socket: Socket;
}

export type BindingHandler = (payload: unknown, context: BindingContext) => void;

interface Binding {
  namespace: Namespace;
  event: string;
  handler: BindingHandler;
}

const bindings = new Set<Binding>();

export function registerBinding(
  namespace: Namespace,
  event: string,
  handler: BindingHandler,
): () => void {
  const binding = { namespace, event, handler };
  bindings.add(binding);
  return () => bindings.delete(binding);
}

export function bindingsFor(namespace: Namespace): Binding[] {
  return [...bindings].filter((binding) => binding.namespace === namespace);
}
